/**
 * Inline SVG payment-method badges used by `PaymentMethodsRow`.
 *
 * Hand-rolled rather than pulled from a CDN so the marketing site
 * has no external image dependency at first paint and so we don't
 * ship an image-tracker URL the moment a visitor lands on /pricing.
 *
 * Each badge is a 36×24 rounded rectangle with the brand mark drawn
 * in monochrome currentColor — the colour is set by the consumer via
 * CSS so it switches with light / dark theme automatically.
 */

import type { CSSProperties, JSX } from 'react';

const baseStyle: CSSProperties = {
  width: 36,
  height: 24,
  border: '1px solid var(--border)',
  borderRadius: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.5,
  color: 'var(--text-muted)',
  background: 'var(--bg)',
  flexShrink: 0,
};

function Plate({ children, label }: { children: React.ReactNode; label: string }): JSX.Element {
  return (
    <span role="img" aria-label={label} style={baseStyle} title={label}>
      {children}
    </span>
  );
}

export const PaymentIcons: Record<string, () => JSX.Element> = {
  card: () => <Plate label="Card">CARD</Plate>,
  visa: () => <Plate label="Visa">VISA</Plate>,
  mastercard: () => <Plate label="Mastercard">MC</Plate>,
  amex: () => <Plate label="American Express">AMEX</Plate>,
  apple_pay: () => <Plate label="Apple Pay">PAY</Plate>,
  google_pay: () => <Plate label="Google Pay">GPay</Plate>,
  ach: () => <Plate label="ACH transfer">ACH</Plate>,
  sepa: () => <Plate label="SEPA transfer">SEPA</Plate>,
  upi: () => <Plate label="UPI">UPI</Plate>,
  netbanking: () => <Plate label="Net banking">NET</Plate>,
  wallet: () => <Plate label="Wallets">WAL</Plate>,
  paypal: () => <Plate label="PayPal">PP</Plate>,
  wire: () => <Plate label="Wire transfer">WIRE</Plate>,
  invoice: () => <Plate label="Invoice / PO">INV</Plate>,
};
